import {
  IonBackButton,
  IonBadge,
  IonButtons,
  IonContent,
  IonHeader,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonList,
  IonMenuButton,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { chevronDownCircleOutline } from "ionicons/icons";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getDeviceMotorTestHistory } from "../services/device.service";

const PAGE_SIZE = 20;

const STATUS_COLOR: Record<string, string> = {
  ACKED: "success",
  SENT: "warning",
  PENDING: "medium",
  NO_ACK: "danger",
  PUBLISH_FAILED: "danger",
};

const DeviceTestHistory: React.FC = () => {
  const location = useLocation();
  const [deviceId, setDeviceId] = useState("");
  const [imei, setImei] = useState("");
  const [items, setItems] = useState<any[]>([]);
  const [skip, setSkip] = useState(0);
  const [disableInfiniteScroll, setDisableInfiniteScroll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPage = async (id: string, nextSkip: number, replace: boolean) => {
    if (!id) return;
    const page = await getDeviceMotorTestHistory(id, nextSkip, PAGE_SIZE).catch(() => []);
    const rows = Array.isArray(page) ? page : [];
    setItems((prev) => (replace ? rows : [...prev, ...rows]));
    setSkip(nextSkip + rows.length);
    setDisableInfiniteScroll(rows.length < PAGE_SIZE);
    setIsLoading(false);
  };

  useEffect(() => {
    const state = location.state as any;
    const id = state?.deviceId ?? "";
    setDeviceId(id);
    setImei(state?.imei ?? "");
    setIsLoading(true);
    fetchPage(id, 0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleRefresh = async (e: CustomEvent) => {
    await fetchPage(deviceId, 0, true);
    (e.target as HTMLIonRefresherElement).complete();
  };

  const fetchNext = async (e: CustomEvent) => {
    await fetchPage(deviceId, skip, false);
    (e.target as HTMLIonInfiniteScrollElement).complete();
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/test-devices" />
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Motor Test History</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent pullingIcon={chevronDownCircleOutline} />
        </IonRefresher>

        <IonItem lines="none">
          <IonLabel>
            <p>IMEI: {imei}</p>
          </IonLabel>
        </IonItem>

        {isLoading && items.length === 0 ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px" }}>
            <IonSpinner />
          </div>
        ) : items.length === 0 ? (
          <IonItem lines="none">
            <IonLabel color="medium">No test commands recorded for this device yet.</IonLabel>
          </IonItem>
        ) : (
          <IonList>
            {items.map((item) => (
              <IonItem key={item.id} lines="full">
                <IonLabel className="ion-text-wrap">
                  <h2>
                    <IonBadge color={STATUS_COLOR[item.status] ?? "medium"}>{item.status}</IonBadge>
                  </h2>
                  <p>
                    {moment(item.sentAt ?? item.created_date).format("DD/MM/YYYY HH:mm:ss")} · Run:{" "}
                    {item.runForSeconds}s · By: {item.created_by ?? "—"}
                  </p>
                  {item.failureReason && <p>Reason: {item.failureReason}</p>}
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}

        <IonInfiniteScroll threshold="100px" disabled={disableInfiniteScroll} onIonInfinite={fetchNext}>
          <IonInfiniteScrollContent loadingText="Loading more..." />
        </IonInfiniteScroll>
      </IonContent>
    </IonPage>
  );
};

export default DeviceTestHistory;
